import { Textarea, TextareaProps } from '@chakra-ui/react';
import { ChangeEvent, KeyboardEvent, useEffect, useRef } from 'react';

interface AutoResizeTextareaProps extends Omit<TextareaProps, 'onChange' | 'onKeyPress'> {
  isDisabled?: boolean;
  placeHolder?: string;
  value: string;
  onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyPress?: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  inputAriaLabel?: string;
  minRows?: number;
  maxRows?: number;
}

/**
 * Textarea component that automatically resizes based on the content
 */
export const AutoResizeTextarea = ({
  isDisabled,
  placeHolder,
  value: inputValue,
  onChange,
  onKeyPress,
  inputAriaLabel,
  minRows = 1,
  maxRows = 5,
  ...props
}: AutoResizeTextareaProps) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const adjustHeight = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to calculate proper scrollHeight
    textarea.style.height = 'auto';

    // Calculate new height
    const lineHeight = parseInt(window.getComputedStyle(textarea).lineHeight);
    const paddingTop = parseInt(window.getComputedStyle(textarea).paddingTop);
    const paddingBottom = parseInt(window.getComputedStyle(textarea).paddingBottom);
    const minHeight = minRows * lineHeight + paddingTop + paddingBottom;
    const maxHeight = maxRows * lineHeight + paddingTop + paddingBottom;

    // Set new height
    const scrollHeight = textarea.scrollHeight;
    textarea.style.height = Math.min(Math.max(minHeight, scrollHeight), maxHeight) + 'px';

    // Enable scrolling if content exceeds maxHeight
    textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
  };

  // Adjust height whenever input value changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(adjustHeight, [inputValue]);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (onChange) {
      onChange(e);
    }
    adjustHeight();
  };

  return (
    <Textarea
      ref={textareaRef}
      isDisabled={isDisabled}
      boxShadow='sm'
      border='1px'
      placeholder={placeHolder}
      value={inputValue}
      onChange={handleChange}
      onKeyPress={onKeyPress}
      aria-label={inputAriaLabel}
      rows={minRows}
      resize='none'
      _focus={{ outline: '2px solid', outlineColor: 'upgrade.blue.500', outlineOffset: '0px' }}
      {...props}
    />
  );
};
